// import React from 'react';
import { motion } from 'framer-motion';
import Navbar from './navbar';
import Footer from './Footer';  // Add this import
import { FiTarget, FiSearch, FiUsers, FiBriefcase, FiAward, FiCode } from 'react-icons/fi';
// import { useNavigate } from 'react-router-dom';
// import { auth } from '../firebase';

const Vision = () => {
  // const navigate = useNavigate();  // Add this hook

 

  const coreValues = [
    {
      icon: <FiTarget className="w-8 h-8" />,
      title: "Precision Discovery",
      description: "Enabling targeted exploration of use cases across specific sectors and sub-sectors with intelligent AI assistance."
    },
    {
      icon: <FiCode className="w-8 h-8" />,
      title: "Agentic Intelligence",
      description: "Leveraging advanced AI agents to understand context, suggest relevant solutions, and adapt to user needs."
    },
    {
      icon: <FiUsers className="w-8 h-8" />,
      title: "User-Centric Approach",
      description: "Designing intuitive interfaces that make complex AI capabilities accessible to everyone."
    }
  ];

  const howItWorksSteps = [
    {
      icon: <FiSearch className="w-6 h-6" />,
      title: "Search & Discover",
      description: "Enter your sector or use case of interest"
    },
    {
      icon: <FiTarget className="w-6 h-6" />,
      title: "AI Analysis",
      description: "Our AI analyzes and categorizes relevant use cases"
    },
    {
      icon: <FiAward className="w-6 h-6" />,
      title: "Curated Results",
      description: "Get detailed, sector-specific insights"
    },
    {
      icon: <FiBriefcase className="w-6 h-6" />,
      title: "Implementation",
      description: "Access practical implementation guides"
    }
  ];

  return (
    <div className="flex flex-col w-screen min-h-screen overflow-x-hidden font-sans">
      {/* Top Gradient Section with Enhanced Design */}
      <div className="relative w-screen min-h-screen bg-gradient-to-br from-indigo-950 via-blue-900 to-purple-900 text-white overflow-x-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '48px 48px'
          }}></div>
        </div>

        {/* Gradient Overlay Orbs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="relative z-10">
          <Navbar />
          <div className="pt-32 pb-24 px-4 flex flex-col items-center justify-center min-h-screen">
            {/* Vision Heading & Subtitle with Enhanced Animations */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="text-center mb-12 max-w-5xl"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="inline-block mb-6 px-6 py-2 bg-orange-500/20 border border-orange-400/30 rounded-full"
              >
                <span className="text-orange-300 text-sm md:text-base font-semibold tracking-wide">
                  OUR VISION
                </span>
              </motion.div>

              <h1 className="text-3xl md:text-5xl lg:text-7xl font-extrabold mb-6 md:mb-8 leading-tight">
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="block bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent"
                >
                  Shaping the Future
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="block text-orange-400 mt-2 md:mt-4 bg-gradient-to-r from-orange-400 to-pink-500 bg-clip-text text-transparent"
                >
                  of AI-Powered Discovery
                </motion.span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="text-base md:text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light"
              >
                We aspire to make sector-specific AI use case discovery seamless, intelligent, and accessible for all organizations worldwide.
              </motion.p>

              {/* Decorative Elements */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 1 }}
                className="mt-12 flex justify-center gap-4"
              >
                <div className="w-20 h-1 bg-gradient-to-r from-transparent via-orange-400 to-transparent rounded-full"></div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* White Section for Core Values & How It Works */}
      <div className="flex-1 w-full bg-gradient-to-b from-gray-50 to-white text-black">
        {/* Core Values Section with Modern Glassmorphism */}
        <section className="py-20 px-4 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent">
              Our Core Values
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Principles that drive our mission forward
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {coreValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.6 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                {/* Glassmorphism overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/10 rounded-bl-full blur-2xl"></div>

                <div className="relative z-10">
                  {/* Icon with animated background */}
                  <div className="mb-6 inline-flex p-4 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl border border-blue-400/30 group-hover:border-orange-400/50 transition-colors duration-300">
                    <div className="text-blue-400 group-hover:text-orange-400 transition-colors duration-300">
                      {value.icon}
                    </div>
                  </div>

                  <h3
                    className="mb-4 group-hover:text-orange-300 transition-colors duration-300"
                    style={{
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 600,
                      fontStyle: 'normal',
                      fontSize: '20px',
                      lineHeight: '150%',
                      letterSpacing: '0',
                      color: 'rgba(255, 255, 255, 0.95)',
                    }}
                  >
                    {value.title}
                  </h3>

                  <p
                    style={{
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '16px',
                      lineHeight: '160%',
                      letterSpacing: '0',
                      color: 'rgba(200, 200, 200, 1)',
                    }}
                  >
                    {value.description}
                  </p>

                  {/* Bottom accent line */}
                  <div className="mt-6 w-16 h-1 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full group-hover:w-full transition-all duration-500"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* How It Works Section with Timeline */}
        <section className="py-20 px-4 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent">
              How It Works
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              A seamless journey from discovery to implementation
            </p>
          </motion.div>

          {/* Desktop Timeline View */}
          <div className="hidden lg:block relative">
            {/* Connection Line */}
            <div className="absolute top-32 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-purple-300 to-pink-300"></div>

            <div className="grid grid-cols-4 gap-6">
              {howItWorksSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  className="relative"
                >
                  {/* Step Number Circle */}
                  <div className="relative z-10 w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                    <span className="text-3xl font-bold text-white">{index + 1}</span>
                  </div>

                  {/* Card */}
                  <motion.div
                    whileHover={{ y: -8, scale: 1.05 }}
                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                  >
                    <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg flex items-center justify-center">
                      <div className="text-blue-600">
                        {step.icon}
                      </div>
                    </div>
                    <h3 className="text-lg font-bold mb-3 text-gray-900 text-center">{step.title}</h3>
                    <p className="text-gray-600 text-sm text-center leading-relaxed">{step.description}</p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Mobile/Tablet View */}
          <div className="lg:hidden relative space-y-8">
            {/* Vertical Line */}
            <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-200 via-purple-300 to-pink-300"></div>

            {howItWorksSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.6 }}
                className="relative flex items-start gap-6"
              >
                {/* Step Number Circle */}
                <div className="relative z-10 flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                  <span className="text-xl font-bold text-white">{index + 1}</span>
                </div>

                {/* Card */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="flex-1 bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                >
                  <div className="w-12 h-12 mb-4 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg flex items-center justify-center">
                    <div className="text-blue-600">
                      {step.icon}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-gray-900">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Future Vision Section with Enhanced Design */}
        <section className="py-20 px-4 max-w-6xl mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 rounded-3xl overflow-hidden shadow-2xl"
          >
            {/* Decorative Background Elements */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/30 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl"></div>
            </div>

            {/* Grid Pattern */}
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
              backgroundSize: '50px 50px'
            }}></div>

            <div className="relative z-10 p-8 md:p-12 lg:p-16 text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-block mb-6 px-6 py-2 bg-orange-500/20 border border-orange-400/30 rounded-full"
              >
                <span className="text-orange-300 text-sm md:text-base font-semibold tracking-wide">
                  FUTURE VISION
                </span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-3xl md:text-5xl font-bold mb-6 text-white"
              >
                Building Tomorrow's Innovation
                <span className="block mt-2 bg-gradient-to-r from-orange-400 to-pink-500 bg-clip-text text-transparent">
                  Ecosystem Today
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-base md:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed"
                style={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 400,
                  lineHeight: '180%',
                }}
              >
                We envision a future where AI-powered discovery becomes the cornerstone of innovation across all sectors. By continually expanding our database of use cases and enhancing our AI capabilities, we aim to create the most comprehensive and intelligent platform for discovering and implementing AI solutions in real-world scenarios.
              </motion.p>

              {/* Decorative Bottom Element */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="mt-10 flex justify-center gap-2"
              >
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </motion.div>
            </div>
          </motion.div>
        </section>
      </div>
      
      {/* Add Footer here */}
      <Footer />
    </div>
  );
};

// Add this new export (content-only version for embedding in About page)
export const VisionContent = () => {
  const coreValues = [
    { icon: <FiTarget className="w-6 h-6" />, title: "Precision Discovery", description: "Enabling targeted exploration of use cases across specific sectors and sub-sectors with intelligent AI assistance." },
    { icon: <FiCode className="w-6 h-6" />, title: "Agentic Intelligence", description: "Leveraging advanced AI agents to understand context, suggest relevant solutions, and adapt to user needs." },
    { icon: <FiUsers className="w-6 h-6" />, title: "User-Centric Approach", description: "Designing intuitive interfaces that make complex AI capabilities accessible to everyone." }
  ];
  // Add a placeholder to form a 2x2 grid matching the design
  const coreValuesGrid = [...coreValues, { placeholder: true }];

  const howItWorksSteps = [
    { icon: <FiSearch className="w-5 h-5" />, title: "Search & Discover", description: "Enter your sector or use case of interest" },
    { icon: <FiTarget className="w-5 h-5" />, title: "AI Analysis", description: "Our AI analyzes and categorizes relevant use cases" },
    { icon: <FiAward className="w-5 h-5" />, title: "Curated Results", description: "Get detailed, sector-specific insights" },
    { icon: <FiBriefcase className="w-5 h-5" />, title: "Implementation", description: "Access practical implementation guides" }
  ];
  return (
    <div className="space-y-10">
      <header>
        <h2
          className="text-[32px] font-semibold"
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600,
            fontStyle: 'normal',
            lineHeight: '150%',
            letterSpacing: '0',
            color: 'rgba(255, 84, 31, 1)',
          }}
        >
          Our Vision
        </h2>
        <div>
          <span
            style={{
              fontFamily: 'Sora, sans-serif',
              fontWeight: 700,
              fontSize: '24px',
              lineHeight: '200%',
              color: 'rgba(255,255,255,1)',
            }}
            className="block mb-2"
          >
            Empowering Discovery Through AI
          </span>
          <span
            style={{
              fontFamily: 'Sora, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '200%',
              color: 'rgba(140, 140, 140, 1)',
            }}
            className="block"
          >
            We’re building the future of sector‑specific use case discovery, leveraging agentic AI to unlock innovative solutions across industries.
          </span>
        </div>
      </header>

      {/* Updated Core Values layout to match provided design (2x2 grid with internal lines) */}
      <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8 relative">
        <h3
          className="text-base md:text-lg font-medium mb-6"
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600,
            fontStyle: 'normal',
            fontSize: '24px',
            lineHeight: '150%',
            letterSpacing: '0',
            color: 'rgba(255, 255, 255, 1)',
          }}
        >
          Our Core Values
        </h3>

        <div className="relative">
          {/* Internal dividing lines (desktop) */}
          <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-px bg-white/10 pointer-events-none" />
            {/* Horizontal line only if we have second row content */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-white/10 pointer-events-none" />

          <div className="grid grid-cols-1 md:grid-cols-2">
            {coreValuesGrid.map((item, i) => {
              const isPlaceholder = (item as any).placeholder;
              return (
                <div
                  key={i}
                  className={`min-h-[160px] flex flex-col justify-start px-2 md:px-4 py-4 md:py-6 ${
                    isPlaceholder ? 'hidden md:block' : ''
                  }`}
                >
                  {!isPlaceholder && (
                    <>
                      <div className="text-orange-400 mb-2">{(item as any).icon}</div>
                      <h4
                        style={{
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontWeight: 700,
                          fontSize: '20px',
                          color: '#fff',
                          marginBottom: '10px',
                          letterSpacing: '0.01em',
                        }}
                        className="mb-2"
                      >
                        {(item as any).title}
                      </h4>
                      <p
                        style={{
                          fontFamily: 'Lora, serif',
                          fontWeight: 400,
                          fontSize: '16px',
                          color: '#ccc',
                          lineHeight: '160%',
                        }}
                      >
                        {(item as any).description}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8">
        <h3
          className="text-base md:text-lg font-medium mb-6"
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600,
            fontStyle: 'normal',
            fontSize: '24px',
            lineHeight: '150%',
            letterSpacing: '0',
            color: 'rgba(255, 255, 255, 1)',
          }}
        >
          How It Works
        </h3>

        <div className="relative">
          {/* Internal dividing lines for desktop */}
          <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-px bg-white/10 pointer-events-none" />
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-white/10 pointer-events-none" />

          <div className="grid grid-cols-1 md:grid-cols-2">
            {howItWorksSteps.map((step, i) => (
              <div
                key={i}
                className="min-h-[140px] flex flex-col justify-start px-2 md:px-4 py-4 md:py-6"
              >
                <h4
                  style={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '18px',
                    lineHeight: '150%',
                    letterSpacing: '0',
                    color: 'rgba(204, 204, 204, 1)',
                  }}
                  className="mb-2"
                >
                  {step.title}
                </h4>
                <p
                  style={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '16px',
                    lineHeight: '150%',
                    letterSpacing: '0',
                    color: 'rgba(140, 140, 140, 1)',
                  }}
                  className="leading-relaxed"
                >
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm p-6 md:p-8">
        <h3
          // apply Sora styles
          className="mb-3"
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600,
            fontStyle: 'normal',
            fontSize: '24px',
            lineHeight: '150%',
            letterSpacing: '0',
            color: 'rgba(255,255,255,1)',
          }}
        >
          Future Vision
        </h3>
        <p
          className="leading-relaxed"
          style={{
            fontFamily: 'Sora, sans-serif',
            fontWeight: 400,
            fontStyle: 'normal',
            fontSize: '16px',
            lineHeight: '150%',
            letterSpacing: '0',
            color: 'rgba(140,140,140,1)',
          }}
        >
          We envision AI‑powered discovery as the cornerstone of innovation. By continually expanding our
          use case knowledge graph and advancing contextual intelligence, we aim to become the most
          comprehensive, adaptive platform for real‑world AI adoption.
        </p>
      </section>
    </div>
  );
};

export default Vision;